import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import { LagreNotat, Notat } from '@/modules/notater/models/types'
import { otNotatClient } from '@/openapi/clients'
import { eventBus } from '@/plugins/eventBus'
import { Ungdom } from '@/modules/ungdommer/models/types'
import { Saksbehandler } from '@/modules/core/models/types'

export const useNotatStore = defineStore('notatStore', () => {
  const lasterNotater = ref<boolean>(false)
  const notater = ref<Notat[]>([])
  const notaterMedUlesteMeldinger = ref<Notat[]>([])
  const { t } = useI18n()

  async function hentUleseNotater() {
    const { data, error } = await otNotatClient.GET('/v1/ot-notater', {
      params: {
        query: { delte: false, egne: false, uleste: true, detaljer: false }
      }
    })
    if (data) {
      notaterMedUlesteMeldinger.value = data.notater
    }
    if (error) {
      Promise.reject(error)
      return
    }
  }

  async function hentNotater(abortController?: AbortController) {
    lasterNotater.value = true

    try {
      const { data, error } = await otNotatClient.GET('/v1/ot-notater', {
        params: {
          query: {
            delte: true,
            egne: true,
            uleste: true,
            detaljer: true
          },
          signal: abortController?.signal
        }
      })
      if (data && data.notater) {
        notater.value = data.notater
      }
      if (error) {
        Promise.reject(error)
        return
      }
    } finally {
      lasterNotater.value = false
    }
  }

  async function lagreNyttNotatMedUngdom(nyttNotat: LagreNotat, gjelder_ungdom: Ungdom) {
    const pathVariables = {
      ungdom_id: gjelder_ungdom.id
    }

    const { data, error } = await otNotatClient.POST('/v1/ot-ungdommer/{ungdom_id}/ot-notater', {
      params: { path: pathVariables },
      body: nyttNotat
    })

    if (data) {
      notater.value.unshift(data)
      eventBus.$emit('showSuccess', t('notater.nyttNotat.ok', 'Nytt notat opprettet'))
    }
    if (error) {
      eventBus.$emit(
        'showError',
        t('notater.nyttNotat.feil', 'En feil oppstod ved lagring av nytt notat')
      )
    }
  }

  async function lagreNyttNotatMedUngdommer(nyttNotat: LagreNotat, ungdommer: Ungdom[]) {
    if (!nyttNotat || !ungdommer) {
      return
    }
    const ungdom_ids: string[] = ungdommer?.map((bruker) => bruker.id) ?? []

    const { data, error } = await otNotatClient.POST('/v1/bulk/ot-notater', {
      body: {
        ungdom_ids: ungdom_ids,
        notat: nyttNotat
      }
    })
    if (ungdommer) {
      const ungdom_navn: any[] = ungdommer.map((ungdom) => ungdom.navn) ?? []

      const navn = ungdom_navn.join(', ') ?? ''

      if (data && data.notater && notater.value) {
        notater.value.concat(data.notater)
        eventBus.$emit('showSuccess', t('notater.nyttNotatValgteUngdommer.ok', [navn]))
      }
      if (error) {
        eventBus.$emit('showError', t('notater.nyttNotatValgteUngdommer.feil', [navn]))
      }
    }
  }

  async function lagreNyttNotat(nyttNotat: LagreNotat) {
    const { data, error } = await otNotatClient.POST('/v1/ot-notater', {
      body: nyttNotat
    })
    if (data) {
      notater.value.unshift(data)
      eventBus.$emit('showSuccess', t('notater.nyttNotat.ok', 'Nytt notat opprettet'))
    }
    if (error) {
      eventBus.$emit(
        'showError',
        t('notater.nyttNotat.feil', 'En feil oppstod ved lagring av nytt notat')
      )
    }
  }

  async function oppdaterNotat(notat: Notat, oppdatering: LagreNotat) {
    const pathVariables = {
      notat_id: notat.id
    }

    const { data, error } = await otNotatClient.POST('/v1/ot-notater/{notat_id}', {
      params: { path: pathVariables },
      body: oppdatering
    })
    if (data) {
      notater.value.splice(notater.value.indexOf(notat), 1, data)
      eventBus.$emit('showSuccess', t('notater.endre.ok', [notat.tittel]))
    }
    if (error) {
      eventBus.$emit('showError', t('notater.endre.feil', [notat.tittel]))
    }
  }

  async function lagreSvar(notat_id: string, html: string) {
    const { data, error } = await otNotatClient.POST('/v1/ot-notater/{notat_id}/svar', {
      params: {
        path: { notat_id: notat_id }
      },
      body: { html: html }
    })

    if (data) {
      const idx = notater.value.findIndex((n) => n.id === notat_id)

      if (idx > -1) {
        notater.value.splice(idx, 1, data)
      }
      eventBus.$emit('showSuccess', t('notater.nyttSvar.ok', 'Nytt svar opprettet'))
      return data
    }
    if (error) {
      eventBus.$emit('showError', t('notater.nyttSvar.feil', 'En feil oppstod ved lagring av svar'))
    }
  }

  async function leggTilMottakere(notat_id: string, mottaker_saksbehandler_ids: any[]) {
    const mottakerBrukerIds = mottaker_saksbehandler_ids?.map((bruker) => bruker.id) ?? []

    const { data, error } = await otNotatClient.POST('/v1/ot-notater/{notat_id}/mottakere', {
      params: {
        path: { notat_id: notat_id }
      },
      body: { mottaker_saksbehandler_ids: mottakerBrukerIds }
    })
    if (data) {
      const idx = notater.value.findIndex((n) => n.id === notat_id)
      if (idx > -1) {
        notater.value.splice(idx, 1, data)
        eventBus.$emit(
          'showSuccess',
          t('notater.leggTilMottakere.ok', [mottaker_saksbehandler_ids.length, data.tittel])
        )
      }
    }
    if (error) {
      eventBus.$emit(
        'showError',
        t('notater.nyttNotat.feil', 'En feil oppstod ved lagring av nytt notat')
      )
    }
  }

  async function slettNotat(notat: Notat) {
    const query = {
      notat_id: notat.id
    }
    const { response } = await otNotatClient.DELETE('/v1/ot-notater/{notat_id}', {
      params: { path: query }
    })

    if (response.ok) {
      const idx = notater.value.findIndex((n) => n.id === notat.id)
      if (idx > -1) {
        notater.value.splice(idx, 1)
        eventBus.$emit('showSuccess', t('notater.slett.ok', [notat.tittel]))
      }
    } else {
      eventBus.$emit('showError', t('notater.slett.feil'))
    }
  }

  async function slettMottaker(notat: Notat, mottaker: Saksbehandler) {
    const query = {
      notat_id: notat.id,
      mottaker_id: mottaker.id
    }
    const { response } = await otNotatClient.DELETE(
      '/v1/ot-notater/{notat_id}/mottakere/{mottaker_id}',
      {
        params: { path: query }
      }
    )
    if (response.ok) {
      const notatFinnes = notater.value.find((n) => n.id === notat.id)

      const idx = notatFinnes?.mottakere?.findIndex((n) => n.bruker?.id === mottaker.id)
      if (idx !== undefined && idx > -1) {
        notatFinnes?.mottakere?.splice(idx, 1)
        eventBus.$emit(
          'showSuccess',
          t('notater.mottaker.slett.ok', [mottaker.etternavn, notat.tittel])
        )
      }
    } else {
      eventBus.$emit('showError', t('notater.mottaker.slett.feil', [mottaker.etternavn]))
    }
  }

  async function slettMegSelv(notat: Notat) {
    const query = {
      notat_id: notat.id
    }
    const { response } = await otNotatClient.DELETE(
      '/v1/ot-notater/{notat_id}/mottakere/meg-selv',
      {
        params: { path: query }
      }
    )

    if (response.ok) {
      // Remove the note from the list since the user is no longer a recipient
      const idx = notater.value.findIndex((n) => n.id === notat.id)
      if (idx > -1) {
        notater.value.splice(idx, 1)
        eventBus.$emit('showSuccess', t('notater.megSelv.slett.ok', [notat.tittel]))
      }
    } else {
      eventBus.$emit('showError', t('notater.megSelv.slett.feil'))
    }
  }

  async function bekreftLest(notat_id: string, lest_id: string) {
    const { data } = await otNotatClient.PUT(
      '/v1/ot-notater/{notat_id}/lese-bekreftelser/{lest_id}',
      {
        params: {
          path: { notat_id: notat_id, lest_id: lest_id }
        }
      }
    )
    if (data) {
      const idx = notater.value.findIndex((n) => n.id === notat_id)
      notater.value.splice(idx, 1, data)
    }
  }

  return {
    lasterNotater,
    notater,
    notaterMedUlesteMeldinger,
    lagreNyttNotat,
    oppdaterNotat,
    leggTilMottakere,
    slettNotat,
    slettMottaker,
    slettMegSelv,
    hentNotater,
    hentUleseNotater,
    lagreSvar,
    bekreftLest,
    lagreNyttNotatMedUngdommer,
    lagreNyttNotatMedUngdom
  }
})
